import React, { useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Dialog } from '@headlessui/react';
import axios from 'axios';

const UNSPLASH_API_URL = 'https://api.unsplash.com';

export default function UnsplashImagePicker({ onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchImages = async (query) => {
    if (!query) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${UNSPLASH_API_URL}/search/photos`, {
        params: {
          query,
          per_page: 20,
          client_id: process.env.REACT_APP_UNSPLASH_ACCESS_KEY
        }
      });
      setImages(response.data.results);
    } catch (error) {
      console.error('Error searching Unsplash:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchImages(search);
  };

  const handleImageSelect = async (image) => {
    try {
      // Trigger Unsplash download event
      await axios.get(image.links.download_location, {
        params: {
          client_id: process.env.REACT_APP_UNSPLASH_ACCESS_KEY
        }
      });
      
      onSelect({
        url: image.urls.regular,
        attribution: {
          name: image.user.name,
          username: image.user.username,
          link: image.user.links.html
        }
      });
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="fixed inset-0 bg-black/50" />
      <div className="relative bg-dark-200 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">Select Stock Image</h2>
          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-white" />
          </button>
        </div>

        <form onSubmit={handleSearch} className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search for images..."
              className="w-full px-4 py-2 pl-10 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary"
            />
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
          </div>
        </form>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((image) => (
                <button
                  key={image.id}
                  onClick={() => handleImageSelect(image)}
                  className="relative group aspect-video overflow-hidden rounded-lg"
                >
                  <img
                    src={image.urls.small}
                    alt={image.alt_description}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                    <div className="text-xs text-white truncate">
                      Photo by {image.user.name}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-gray-400 text-center">
          Powered by Unsplash
        </div>
      </div>
    </Dialog>
  );
} 